import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange } from '../../range-validate';
import { FdCalculatorService, FdResult, FD_CONSTRAINTS } from './fd-calculator.service';

@Component({
  selector: 'app-fd-calculator',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  providers: [InvestmentChartService],
  templateUrl: './fd-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./fd-calculator.scss']
})
export class FdCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(FdCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('breakdownRef') breakdownRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('growthRef')    growthRef!:    ElementRef<HTMLCanvasElement>;

  readonly C = FD_CONSTRAINTS;
  readonly Math = Math;

  principal = 100_000;
  annualRate = 7;
  tenureYears = 5;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     FdResult | null = null;

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.principal, this.C.principal.min, this.C.principal.max, 'Deposit amount');
    if (e1) errors['principal'] = e1;
    const e2 = validateRange(this.annualRate, this.C.rate.min, this.C.rate.max, 'Interest rate');
    if (e2) errors['annualRate'] = e2;
    const e3 = validateRange(this.tenureYears, this.C.tenureYears.min, this.C.tenureYears.max, 'Tenure');
    if (e3) errors['tenureYears'] = e3;
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = { principal: true, annualRate: true, tenureYears: true };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate(this.principal, this.annualRate, this.tenureYears);
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderBreakdown(this.breakdownRef.nativeElement, [
        { label: 'Deposit Amount', value: r.principal, color: '#3b82f6' },
        { label: 'Interest Earned', value: r.totalInterest, color: '#10b981' }
      ], 'Maturity Value');
      this.charts.renderGrowth(
        this.growthRef.nativeElement,
        r.quarters.map(row => `Q${row.quarter}`),
        [
          { label: 'Balance', data: r.quarters.map(row => Math.round(row.balance)), color: '#3b82f6' }
        ]
      );
    }, 60);
  }

  reset(): void {
    this.principal      = 100_000;
    this.annualRate     = 7;
    this.tenureYears    = 5;
    this.formErrors     = {};
    this.touched        = {};
    this.showFormErrors = false;
    this.calculated     = false;
    this.result         = null;
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
